import React, { memo, useCallback, useMemo } from 'react';

import { DiagonalHatchDefinition } from '@da-g3-acl/shared/common';
import { RGB } from '@da-g3-acl/shared/common/colour-types';

import { sanitiseId } from '../../../../../helpers/sanitiseId';
import { isYIQDark, RGBToHex } from '../../../../../types/colour';
import { LengthRange } from '../../../../../types/lengthRange';
import { LoadingGradientDefinition } from '../../svgFills';
import {
	GradientsNumericLaneRowDisplayConfiguration,
	NumericLaneRowConfiguration,
	NumericPoint,
} from '../configuration';
import { GreyscaleProvider, useGradients } from '../useGradients';

export interface GradientsLaneRowProps {
	id: string;
	configuration: Omit<NumericLaneRowConfiguration, 'displayConfiguration'>;
	display: GradientsNumericLaneRowDisplayConfiguration;
	width: number;
	heightInPx: number;
	scale: number;
	lane: LengthRange;
	position: number;
}

type GradientInterval = { from: NumericPoint; to: NumericPoint };

interface InterpolatedGradientPoint {
	at: number;
	colour: RGB;
	value?: number;
}

const GradientNumericLaneRowInternal: React.FC<GradientsLaneRowProps> = ({
	id,
	width,
	heightInPx,
	position,
	display,
	lane,
	configuration,
	scale,
}) => {
	const sanitizedConfigurationId = sanitiseId(configuration.id);
	const { points, isLoading, valueBounds: bounds } = configuration;

	const gradientProvider = useMemo(
		() => (display.colourProvider ?? GreyscaleProvider)(bounds).sort((a, b) => a.value - b.value),
		[bounds, display.colourProvider],
	);

	const { normalisedProvider, getNormalisedValue, getColourForNormalisedValue, getRealValue, getColourForRealValue } =
		useGradients(gradientProvider, bounds);

	// Gradient stops finds all the transition points that are required for a gradient interval
	// If the start/end does not land on a discrete gradient point, it also performs a linear interpolation
	// as the SVG gradient declaration does not support gradient stop points that exceed the shape being drawn
	const gradientStops = useCallback(
		(interval: GradientInterval): InterpolatedGradientPoint[] => {
			// If there is no gradient, return the minimum viable colour
			const intervalPoints: InterpolatedGradientPoint[] = [];
			if (!gradientProvider.length) return [];
			if (gradientProvider.length === 1)
				return [
					{ at: interval.from.at, colour: gradientProvider[0].colour },
					{ at: interval.to.at, colour: gradientProvider[0].colour },
				];
			const intervalLength = interval.to.at - interval.from.at;

			const normalisedIntervalFromValue = getNormalisedValue(interval.from.value);
			const normalisedIntervalToValue = getNormalisedValue(interval.to.value);

			// We use the linear form crossing two points to get fn(x) and fn(y)
			// x - depth of the interval, 0 = start, 1 = end
			// y - normalised colour within the gradient provider declaration

			// fn(y) gets the position DH of any colour crossover points that we need to add to our gradient
			// fn(x) gets the colour at the ends (i.e: x =0 & x = 1)

			// The benefit of using a linear function is that we can calculate colours below irrespective of whether the value is increasing or decreasing as the gradient interval moves downhole
			const normalisedIntervalValueRange = normalisedIntervalToValue - normalisedIntervalFromValue;
			const yFn = (x: number): number =>
				(normalisedIntervalToValue - normalisedIntervalFromValue) * x + normalisedIntervalFromValue;
			const xFn = (y: number): number => (y - normalisedIntervalFromValue) / normalisedIntervalValueRange;
			// Sometimes our interval is not fully shown. We must provide the crossover gradient points. This is because the underlying render of the gradients within the SVG must have offset 0 <= x <= 1
			// Therefore, we must calculate the gradient colour at these limits but still retain the untrimmed interval
			const xStart = (Math.max(interval.from.at, lane.depthFrom) - interval.from.at) / intervalLength;
			const xEnd = (Math.min(interval.to.at, lane.depthTo) - interval.from.at) / intervalLength;
			const yStart = yFn(xStart);
			const yEnd = yFn(xEnd);

			// If our interval is constant, that is, start and end are the same value, then treat as a flat colour
			if (!normalisedIntervalValueRange)
				return [xStart, xEnd].map((x) => ({
					at: interval.from.at + x * intervalLength,
					colour: getColourForNormalisedValue(normalisedIntervalFromValue),
					value: getRealValue(normalisedIntervalFromValue),
				}));
			// Get all gradient points inside our interval. If the crossover is not within our interval range (i.e not 0 < x < 1) then we don't need it
			const internalGradientPoints = normalisedProvider
				.map((gp) => ({ threshold: gp, x: xFn(gp.value) }))
				.filter(({ x }) => x >= xStart && x <= xEnd)
				.sort((a, b) => a.x - b.x);

			// If the start does not exactly land on a gradient colour transition point, we must interpolate it using the above function
			if (
				yStart > 0 &&
				yStart < 1 &&
				!internalGradientPoints.find(({ threshold }) => threshold.value === yStart)
			) {
				// we must find the point below, and above, and interpolate
				const colour = getColourForNormalisedValue(yStart);
				intervalPoints.push({ ...interval.from, colour });
			}

			// Add any internal gradient points that were crossed between the start and end of the interval
			intervalPoints.push(
				...internalGradientPoints.map(({ threshold, x }) => ({
					at: interval.from.at + x * intervalLength,
					colour: threshold.colour,
					value: getRealValue(threshold.value),
				})),
			);

			// If the end does not exactly land on a gradient colour transition point, we must interpolate it using the above function
			if (yEnd > 0 && yEnd < 1 && !internalGradientPoints.find(({ threshold }) => threshold.value === yEnd)) {
				// we must find the point below, and above, and interpolate
				const colour = getColourForNormalisedValue(yEnd);
				intervalPoints.push({ ...interval.to, colour });
			}
			return intervalPoints;
		},
		[getColourForNormalisedValue, getNormalisedValue, getRealValue, gradientProvider, normalisedProvider, lane],
	);

	const leftMostPointIndex = points.findIndex(({ at }) => at > lane.depthFrom);
	const rightMostPointIndex = points.findIndex(({ at }) => at > lane.depthTo);
	const pointsDataRange: LengthRange | undefined =
		points.length > 0
			? {
					depthFrom: points[0].at,
					depthTo: points[points.length - 1].at,
				}
			: undefined;

	const pointsToRender = useMemo(
		() =>
			points.slice(
				leftMostPointIndex < 1 ? 0 : leftMostPointIndex - 1,
				rightMostPointIndex === -1 ? undefined : rightMostPointIndex + 1,
			),
		[leftMostPointIndex, rightMostPointIndex, points],
	);

	/**
	 * Given two points, calculate the interval that would be between them. We use this interval, and its delta (change)
	 * in value to determine intermediate colour points that must be interpolated from the gradient provider in the configuration.
	 * If the points lie  between two colours on the provider definition, we must lineraly interpolate what this colour would
	 * be using a simple linear intersection on each of the RGB channelsintersection on each of the RGB channels
	 * @type {GradientInterval[]}
	 */
	const gradientIntervalPoints: InterpolatedGradientPoint[] = useMemo(
		() =>
			// Generate intervals for all points, or the gaps therein. Only the space between points has an interval generated
			pointsToRender
				.reduce<GradientInterval[]>((previousValue, currentValue, currentIndex, array) => {
					if (currentIndex === 0) return [];
					return [...previousValue, { from: array[currentIndex - 1], to: currentValue }];
				}, [])
				/*
				Now the fun part, we must calculate the colours at each point in our interval, both at the extremes (start + end) and any intermediate points where the colour change
				EG: Imagine this colour gradient:

				0.0 = RED
				0.5 = GREEN
				1.0 = BLUE

				and these two values which constitute the interval we are colour grading:
				Start = 0.2
				End = 1

				Then we must create a gradient between these points:
				Start (0.0) = Yellow-ish (gradient between red and green)
				Green Crossover (0.5) = Green
				End (1.0) Blue
				 */
				.reduce<InterpolatedGradientPoint[]>((prev, curr, idx) => {
					let stops = gradientStops(curr);
					// If we are not at the first interval, then skip the from colour because it has already been added to the list
					// (i.e: end of previous interval)
					if (idx) [, ...stops] = stops;
					return [...prev, ...stops];
				}, []),
		[gradientStops, pointsToRender],
	);

	const pointIndicatorsVisible =
		display.showPoints &&
		(!configuration.pointIndicatorConfig || scale > configuration.pointIndicatorConfig.thresholdScale);

	// Check if our lane has some or all of our points in it. Any lanes outside of this will not contain a rectangle
	const pointsInLane =
		pointsToRender.length &&
		pointsDataRange &&
		pointsDataRange.depthTo > lane.depthFrom &&
		pointsDataRange.depthFrom < lane.depthTo;

	const gradientX = pointsInLane ? Math.max(0, pointsDataRange.depthFrom - lane.depthFrom) * scale : 0;
	const gradientWidth = pointsInLane
		? (Math.min(lane.depthTo, pointsDataRange.depthTo) - Math.max(lane.depthFrom, pointsDataRange.depthFrom)) *
			scale
		: width;

	return (
		<svg width={width} height={heightInPx} x={0} y={position}>
			<defs>
				<DiagonalHatchDefinition id="diagonalHatch" />
				<LoadingGradientDefinition id="loadingPulseGradient" />
				{/* Only render the gradient if we have points in the lane. Otherwise, the gradient will be blank and we don't need it */}
				{pointsInLane && (
					<linearGradient
						key={`${sanitizedConfigurationId}--gradient-${lane.depthFrom}`}
						id={`gradient-${sanitizedConfigurationId}-${lane.depthFrom}`}
					>
						{gradientIntervalPoints.map(({ at, colour, value }, i) => {
							// The gradient is in the range of 0-100% (i.e 0.0 to 1.0) so normalise depths across the lane range
							const offset = Math.min(
								1,
								Math.max(
									0,
									(at - Math.max(lane.depthFrom, pointsDataRange.depthFrom)) /
										(Math.min(lane.depthTo, pointsDataRange.depthTo) -
											Math.max(lane.depthFrom, pointsDataRange.depthFrom)),
								),
							);
							return (
								<stop
									key={`${sanitizedConfigurationId}--gradient-stop-${offset}-${i}`}
									offset={offset}
									stopColor={colour}
									data-value={value}
									data-at={at}
								></stop>
							);
						})}
					</linearGradient>
				)}
			</defs>
			{pointsInLane && (
				<rect
					x={gradientX}
					y={0}
					width={gradientWidth}
					height={heightInPx}
					fill={`url(#gradient-${sanitizedConfigurationId}-${lane.depthFrom})`}
				/>
			)}
			{pointIndicatorsVisible &&
				pointsInLane &&
				pointsToRender.map((p, i) => {
					const x = (p.at - lane.depthFrom) * scale;
					const indicatorColour = isYIQDark(RGBToHex(getColourForRealValue(p.value))) ? '#fff' : '#000';
					const pointIndicatorSizePx = configuration.pointIndicatorConfig?.sizePx ?? 5;

					return (
						<path
							d={`M ${x} 0 l -${pointIndicatorSizePx} ${pointIndicatorSizePx} l ${2 * pointIndicatorSizePx} 0 Z`}
							fill={indicatorColour}
							key={`${id}--${sanitizedConfigurationId}--point-indicator-${p.at}-${i}`}
							onMouseEnter={() =>
								configuration.onHover?.({ point: p, colour: getColourForRealValue(p.value) })
							}
							onMouseLeave={() => configuration.onHover?.(undefined)}
						></path>
					);
				})}
			{isLoading && (
				<>
					<rect x={0} y={0} width={width} height={heightInPx} fill="url(#loadingPulseGradient)"></rect>
					<rect x={0} y={0} width={width} height={heightInPx} fill="url(#diagonalHatch)"></rect>
				</>
			)}
		</svg>
	);
};

export const GradientNumericLaneRow = memo(GradientNumericLaneRowInternal);
